import type { NoteModel, FinanceFiguresInput } from "@/models/underwriting";
import type { VehicleDetailModel } from "@/models/vehicle";

export interface HistoryVehicleFinance {
  vehicle: Pick<VehicleDetailModel, "regNumber" | "mileage">;
  finance: FinanceFiguresInput;
}

function numericValue(value: string | undefined): number | null {
  if (!value) return null;
  const cleaned = value.replace(/[£,]/g, "").trim();
  if (!/^\d+(?:\.\d+)?$/.test(cleaned)) return null;
  const number = Number(cleaned);
  return Number.isFinite(number) ? number : null;
}

function parseHistoryComment(comment: string): HistoryVehicleFinance | null {
  const document = new DOMParser().parseFromString(comment, "text/html");
  document.body.querySelectorAll("br").forEach((breakElement) => breakElement.replaceWith("\n"));
  const lines = (document.body.textContent ?? "").split(/\r?\n/).map((line) => line.trim());
  if (!lines.some((line) => line === "Finance Details") || !lines.some((line) => /^Vehicle:?$/.test(line))) return null;

  const sections = new Map<string, Map<string, string>>();
  let fields: Map<string, string> | undefined;
  for (const line of lines) {
    const heading = line.match(/^(Finance Details|Vehicle|PX Details|PX Vehicle):?$/i);
    if (heading) {
      fields = new Map<string, string>();
      sections.set(heading[1].toLowerCase(), fields);
      continue;
    }
    const match = line.match(/^([^:]+):\s*(.*)$/);
    if (match && fields) fields.set(match[1].trim().toLowerCase(), match[2].trim());
  }

  const financeFields = sections.get("finance details");
  const vehicleFields = sections.get("vehicle");
  const price = numericValue(vehicleFields?.get("price"));
  const deposit = numericValue(financeFields?.get("deposit"));
  const term = numericValue(financeFields?.get("term"));
  const regNumber = vehicleFields?.get("vrm");
  if (price == null || deposit == null || !term || !Number.isInteger(term) || !regNumber) return null;
  const partExchange = numericValue(sections.get("px details")?.get("px value"))
    ?? numericValue(sections.get("px vehicle")?.get("price"))
    ?? 0;
  const settlement = numericValue(sections.get("px details")?.get("settlement")) ?? 0;

  return {
    vehicle: {
      regNumber,
      mileage: numericValue(vehicleFields?.get("mileage")),
    },
    finance: {
      cashPrice: price,
      deposit,
      partExchange,
      settlement,
      loanAmount: Math.max(0, price - deposit - partExchange + settlement),
      term,
      annualMileage: numericValue(vehicleFields?.get("annualmileage")) ?? 8000,
    },
  };
}

export function vehicleFinanceFromHistory(notes: NoteModel[]): HistoryVehicleFinance | null {
  const candidates = notes.filter(({ comment }) => /\bfinance\b/i.test(comment) && /\bvehicle\b/i.test(comment));
  for (const note of candidates.sort((a, b) => b.commentDate.localeCompare(a.commentDate))) {
    const result = parseHistoryComment(note.comment);
    if (result) return result;
  }
  return null;
}